import { useId, useMemo, useRef, useState } from 'react';
import { FloatingLayer } from '@/components/shared/FloatingLayer';
import { cn } from '@/utils/cn';

export interface AppSelectOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface AppSelectProps<T extends string> {
  value?: T;
  options: AppSelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (value: T) => void;
}

export function AppSelect<T extends string>({
  value,
  options,
  placeholder = 'Select an option',
  disabled = false,
  ariaLabel,
  onChange,
}: AppSelectProps<T>): JSX.Element {
  const listboxId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);

  return (
    <div className={cn('app-select', disabled && 'app-select--disabled')}>
      <button
        ref={triggerRef}
        type="button"
        className={cn('app-select__trigger', isOpen && 'app-select__trigger--open')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isOpen) {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        <div className="app-select__value">
          <strong>{selectedOption?.label ?? placeholder}</strong>
          <span>{selectedOption?.description ?? (selectedOption ? 'Selected' : 'Choose an option')}</span>
        </div>
        <span className="app-select__caret" aria-hidden="true">
          <span />
        </span>
      </button>

      <FloatingLayer
        open={isOpen}
        anchorRef={triggerRef}
        id={`${listboxId}-surface`}
        className="app-select__menu"
        matchAnchorWidth
        onDismiss={() => setIsOpen(false)}
      >
        {({ maxHeight }) => (
          <div id={listboxId} className="app-select__options" role="listbox" aria-label={ariaLabel} style={{ maxHeight }}>
            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    'app-select__option',
                    isSelected && 'app-select__option--selected',
                    option.disabled && 'app-select__option--disabled',
                  )}
                  disabled={option.disabled}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    window.requestAnimationFrame(() => triggerRef.current?.focus());
                  }}
                >
                  <strong>{option.label}</strong>
                  {option.description ? <span>{option.description}</span> : null}
                </button>
              );
            })}
          </div>
        )}
      </FloatingLayer>
    </div>
  );
}
